/**
 * Default Mode App - Routes for the standard NGUI interface.
 *
 * Contains ONLY the <Routes> tree extracted from the original AppContent.
 * VoiceInterface is NOT here — it stays in App.tsx level.
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute, AdminRoute, PermissionRoute } from '../../components/auth/RouteGuards';

import Login from '../../components/Login';
import CalendarModule from '../../components/modules/CalendarModule';
import KnowledgeBaseModule from '../../components/modules/KnowledgeBaseModule';
import TasksModule from '../../components/modules/TasksModuleV2';
import { EmailLayout } from '../../components/email';
import FilesModule from '../../components/modules/FilesModule';
import {
  PeopleLayout,
  ContactDetailPage,
  CompanyDetailPage,
  CreateContactForm,
  CreateCompanyForm,
} from '../../components/people';
import AdminPanel from '../../components/AdminPanel';
import UserDetailPage from '../../components/admin/UserDetailPage';
import UserStatsPage from '../../components/admin/UserStatsPage';
import SettingsPage from '../../components/SettingsPage';
import BillingPage from '../../components/BillingPage';
import SupportPage from '../../components/SupportPage';
import SteeringPage from '../../components/SteeringPage';
import { TaskDetailPage } from '../../components/tasks';
import FilePreview from '../../components/FilePreview';
import { ChatModule } from '../../components/chat';
import { ContractorsLayout } from '../../components/contractors';

const DefaultModeApp: React.FC = () => {
  return (
    <Routes>
      {/* Login Route */}
      <Route path="/login" element={<Login />} />

      {/* Voice Interface Route (Protected) - Placeholder since VoiceInterface is persistent */}
      <Route
        path="/voice"
        element={
          <ProtectedRoute>
            <div />
          </ProtectedRoute>
        }
      />

      {/* Module Routes (Protected + Permission Checked) */}
      <Route
        path="/calendar"
        element={
          <PermissionRoute permission="calendar">
            <CalendarModule />
          </PermissionRoute>
        }
      />
      <Route
        path="/knowledge"
        element={
          <PermissionRoute permission="knowledge_base">
            <KnowledgeBaseModule />
          </PermissionRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <PermissionRoute permission="tasks">
            <TasksModule />
          </PermissionRoute>
        }
      >
        <Route path=":taskId" element={<TaskDetailPage />} />
      </Route>
      {/* Email Module with nested routes */}
      <Route
        path="/email"
        element={
          <PermissionRoute permission="email">
            <EmailLayout />
          </PermissionRoute>
        }
      >
        <Route index element={null} />
        <Route path="account/:accountId" element={null} />
        <Route path="account/:accountId/folder/:folderId" element={null} />
        <Route path="thread/:threadId" element={null} />
        <Route path="compose" element={null} />
        <Route path="drafts" element={null} />
        <Route path="search" element={null} />
      </Route>
      <Route
        path="/disk"
        element={
          <PermissionRoute permission="disk">
            <FilesModule />
          </PermissionRoute>
        }
      >
        <Route path=":fileId/preview" element={<FilePreview mode="files" />} />
      </Route>
      {/* People & Companies Module */}
      <Route
        path="/people"
        element={
          <PermissionRoute permission="people">
            <PeopleLayout />
          </PermissionRoute>
        }
      >
        <Route index element={<Navigate to="contacts" replace />} />
        <Route path="contacts" element={null} />
        <Route path="contacts/new" element={<CreateContactForm />} />
        <Route path="contacts/:contactId" element={<ContactDetailPage />} />
        <Route path="contacts/:contactId/:tab" element={<ContactDetailPage />} />
        <Route path="companies" element={null} />
        <Route path="companies/new" element={<CreateCompanyForm />} />
        <Route path="companies/:companyId" element={<CompanyDetailPage />} />
        <Route path="companies/:companyId/:tab" element={<CompanyDetailPage />} />
      </Route>
      {/* Chat Module */}
      <Route
        path="/chat"
        element={
          <PermissionRoute permission="chat">
            <ChatModule />
          </PermissionRoute>
        }
      />
      {/* Contractors Module */}
      <Route
        path="/contractors/*"
        element={
          <PermissionRoute permission="contractors">
            <ContractorsLayout />
          </PermissionRoute>
        }
      />
      {/* Backward compatibility redirect */}
      <Route path="/files" element={<Navigate to="/disk" replace />} />
      <Route path="/files/*" element={<Navigate to="/disk" replace />} />

      {/* Settings Route */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Billing Route */}
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />

      {/* Support Route */}
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <SupportPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Panel Route */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        }
      />

      {/* User Detail Page (Admin Only) */}
      <Route
        path="/admin/users/:userId"
        element={
          <AdminRoute>
            <UserDetailPage />
          </AdminRoute>
        }
      />

      {/* User Stats Page */}
      <Route
        path="/admin/users/:userId/stats"
        element={
          <AdminRoute>
            <UserStatsPage />
          </AdminRoute>
        }
      />

      {/* Secret STEERING Monitor */}
      <Route
        path="/adminsteering"
        element={
          <AdminRoute>
            <SteeringPage />
          </AdminRoute>
        }
      />

      {/* File Preview Routes */}
      <Route
        path="/tasks/:taskId/attachments/:attachmentId/preview"
        element={
          <PermissionRoute permission="tasks">
            <FilePreview mode="attachment" />
          </PermissionRoute>
        }
      />
      <Route
        path="/tasks/:taskId/iterations/:iterationId/attachments/:attachmentId/preview"
        element={
          <PermissionRoute permission="tasks">
            <FilePreview mode="iteration" />
          </PermissionRoute>
        }
      />

      {/* Default Route - Redirect to Voice Interface */}
      <Route path="/" element={<Navigate to="/voice" replace />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/voice" replace />} />
    </Routes>
  );
};

export default DefaultModeApp;
